<script>
import { getDepartments } from '@/api/dealer'
import groupBy from 'lodash/groupBy'
import ActionButton from '@/components/ui/buttons/ActionButton.vue'
import ActionButtonDivider from '@/components/ui/buttons/ActionButtonDivider.vue'
import ActionButtonItem from '@/components/ui/buttons/ActionButtonItem.vue'
import TaskTemplateDragHandle from '@/components/tasks/form/TaskTemplateDragHandle.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import FeatureTextarea from '@/components/cars/edit/FeatureTextarea.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    'mia-action-button-divider': ActionButtonDivider,
    'mia-action-button-item': ActionButtonItem,
    'mia-action-button': ActionButton,
    'mia-feature-input': FeatureInput,
    'mia-feature-select': FeatureSelect,
    'mia-feature-textarea': FeatureTextarea,
    'mia-task-template-drag-handle': TaskTemplateDragHandle
  },

  model: {
    prop: 'task',
    event: 'update:task'
  },

  props: {
    task: {
      type: Object,
      required: true
    },

    tasks: {
      type: Array,
      required: true
    },

    draggable: {
      type: Boolean,
      default: false
    },

    dragHandleClass: {
      type: String,
      default: 'drag-handler'
    },

    highlight: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    isBeingDragged: false,
    isCollapsed: false,
    internalDepartments: [],
    deadlineUnits: [
      {
        id: 1,
        item_unit: 'minutes',
        item_title: 'Minuten'
      },
      {
        id: 2,
        item_unit: 'hours',
        item_title: 'Stunden'
      },
      {
        id: 3,
        item_unit: 'days',
        item_title: 'Tage'
      },
      {
        id: 4,
        item_unit: 'weeks',
        item_title: 'Wochen'
      },
      {
        id: 5,
        item_unit: 'months',
        item_title: 'Monate'
      }
    ],
    idleUnits: [
      {
        id: 1,
        item_unit: 'minutes',
        item_title: 'Minuten'
      },
      {
        id: 2,
        item_unit: 'hours',
        item_title: 'Stunden'
      },
      {
        id: 3,
        item_unit: 'days',
        item_title: 'Tage'
      },
      {
        id: 4,
        item_unit: 'weeks',
        item_title: 'Wochen'
      },
      {
        id: 5,
        item_unit: 'months',
        item_title: 'Monate'
      }
    ]
  }),

  computed: {
    ...mapGetters('tasks', ['autoAssignRules', 'inputFields']),

    selectableTasks() {
      return this.tasks.filter(task => task.id !== this.task.id)
    },

    groupedAutoAssignRules() {
      return groupBy(this.autoAssignRules, 'item_group')
    },

    firstAutoAssignRule() {
      return this.autoAssignRules[0]
    },

    firstDeadlineUnit() {
      return this.deadlineUnits[0]
    },

    firstIdleUnit() {
      return this.idleUnits[0]
    },

    groupedInputFields() {
      return groupBy(this.availableInputFields, 'item_group')
    },

    inputFieldsInUse() {
      return this.task.item_input_fields.map(inputField => inputField.id)
    },

    availableInputFields() {
      return this.inputFields.filter(inputFieldConfig => !this.inputFieldsInUse.includes(inputFieldConfig.item_field))
    },

    hasAvailableInputFields() {
      return this.availableInputFields.length > 0
    }
  },

  created() {
    this.loadInternalDepartments()
  },

  methods: {
    async loadInternalDepartments() {
      const [error, response] = await getDepartments()

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Interne Abteilungen konnten nicht geladen werden']
        })
        return
      }

      this.internalDepartments = response
    },

    updateTaskProperties(properties) {
      this.$emit('update:task', {
        ...this.task,
        ...properties
      })
    },

    addInputField(field) {
      this.updateTaskProperties({
        item_input_fields: [
          ...this.task.item_input_fields,
          { id: field.item_field, item_value: '', item_is_required: 'N' }
        ]
      })

      this.$refs.inputFieldActionButtonRef.close()
    },

    deleteInputField(field) {
      this.updateTaskProperties({
        item_input_fields: this.task.item_input_fields.filter(inputField => inputField.id !== field.id)
      })

      this.$refs.inputFieldActionButtonRef.close()
    },

    getInputFieldLabel(inputFieldId) {
      return this.inputFields.find(inputField => inputField.item_field === inputFieldId)?.item_title
    },

    deleteTask() {
      this.$emit('delete-task')
    },

    onTitleInput(value) {
      this.updateTaskProperties({
        item_title: value
      })
    },

    onDepartmentInput(value) {
      this.updateTaskProperties({
        item_department_id: value
      })
    },

    onDescriptionInput(value) {
      this.updateTaskProperties({
        item_description: value
      })
    },

    onAutoAssignInput(isChecked) {
      this.updateTaskProperties({
        item_auto_assign: isChecked ? 'Y' : 'N',
        item_auto_assign_rule: isChecked ? this.firstAutoAssignRule.item_rule : null
      })
    },

    onUniqueInput(isChecked) {
      this.updateTaskProperties({
        item_unique: isChecked ? 'Y' : 'N'
      })
    },

    onAutoAssignRuleInput(value) {
      this.updateTaskProperties({
        item_auto_assign_rule: value
      })
    },

    onInputFieldIsRequiredInput(inputFieldConfig, isChecked) {
      this.updateTaskProperties({
        item_input_fields: this.task.item_input_fields.map((inputField) => {
          if (inputField.id === inputFieldConfig.id) {
            return {
              ...inputField,
              item_is_required: isChecked ? 'Y' : 'N'
            }
          }

          return inputField
        })
      })
    },

    onDeadlineInput(isChecked) {
      this.updateTaskProperties({
        item_deadline: isChecked ? 'Y' : 'N',
        item_deadline_unit: isChecked ? this.firstDeadlineUnit.item_unit : null,
        item_deadline_value: isChecked ? 0 : null
      })

      if (isChecked) {
        this.$nextTick(() => {
          this.$refs.deadlineValueInputRef.focus()
          this.$refs.deadlineValueInputRef.select()
        })
      }
    },

    onIdleInput(isChecked) {
      this.updateTaskProperties({
        item_idle: isChecked ? 'Y' : 'N',
        item_idle_unit: isChecked ? this.firstIdleUnit.item_unit : null,
        item_idle_value: isChecked ? 0 : null
      })

      if (isChecked) {
        this.$nextTick(() => {
          this.$refs.idleValueInputRef.focus()
          this.$refs.idleValueInputRef.select()
        })
      }
    },

    onInformDepartmentHeadInput(value) {
      this.updateTaskProperties({
        item_inform_department_head: value ? '1' : '0'
      })
    },

    onDeadlineValueInput(value) {
      this.updateTaskProperties({
        item_deadline_value: value
      })
    },

    onDeadlineUnitInput(value) {
      this.updateTaskProperties({
        item_deadline_unit: value
      })
    },

    onIdleValueInput(value) {
      this.updateTaskProperties({
        item_idle_value: value
      })
    },

    onIdleUnitInput(value) {
      this.updateTaskProperties({
        item_idle_unit: value
      })
    },

    onTimeValueInput(value) {
      this.updateTaskProperties({
        item_time_value: value
      })
    },

    onPriorityInput(value) {
      this.updateTaskProperties({
        item_priority: value
      })
    },

    onRequiredTasksInput(value) {
      this.updateTaskProperties({
        item_tasks_required: value
      })
    },

    expand() {
      this.isCollapsed = false
    },

    collapse() {
      this.isCollapsed = true
    }
  }
}
</script>

<template>
  <div
    class="border rounded-sm p-4 flex"
    :class="highlight ? 'bg-orange-100 border-orange-300': 'bg-gray-200 border-gray-300'"
    :data-task-id="task.id"
  >
    <div v-if="draggable">
      <mia-task-template-drag-handle :class="dragHandleClass" />
    </div>
    <div
      class="flex-1"
      :class="{ 'mx-3': draggable, 'mr-3': !draggable }"
    >
      <div
        v-if="isCollapsed"
        class="mt-3"
      >
        <h4 class="my-0 py-0">
          {{ task.item_title }}
        </h4>
      </div>
      <div v-else>
        <button
          class="text-gray-700 absolute right-0 top-0 w-10 h-10 flex items-center justify-center focus:outline-none hover:text-gray-900"
          type="button"
          @click="isCollapsed = true"
        >
          <mia-streamline-icon
            class="w-6 h-6 transform rotate-180 fill-current"
            src="arrow-up-1"
          />
        </button>

        <div class="flex">
          <mia-feature-input
            type="text"
            class="flex-1"
            label="Aufgabentitel"
            :value="task.item_title"
            @input="onTitleInput"
          />
          <mia-feature-select
            class="ml-3"
            label="Interne Abteilung"
            @input="onDepartmentInput"
          >
            <template #options>
              <option
                value=""
                disabled
                :selected="true"
              >
                Abteilung wählen
              </option>
              <option
                v-for="department in internalDepartments"
                :key="department.id"
                :value="department.id"
                :selected="department.id === task.item_department_id"
              >
                {{ department.item_name }}
              </option>
            </template>
          </mia-feature-select>
        </div>

        <mia-feature-textarea
          class="mt-4"
          label="Aufgabenbeschreibung"
          :value="task.item_description"
          @input="onDescriptionInput"
        />

        <div class="mt-4 grid grid-cols-2 gap-4">
          <div class="flex">
            <div class="flex items-center">
              <label class="flex flex-col">
                <span class="mb-1 tracking-wider text-gray-800">Automatisch zuweisen</span>

                <div>
                  <mia-toggle
                    :value="task.item_auto_assign === 'Y'"
                    :labels="{
                      checked: 'Ja',
                      unchecked: 'Nein'
                    }"
                    :width="80"
                    :font-size="13"
                    @input="onAutoAssignInput"
                  />
                </div>
              </label>
            </div>

            <div
              v-if="task.item_auto_assign === 'Y'"
              class="ml-6"
            >
              <mia-feature-select
                :value="task.item_auto_assign_rule"
                label="Automatisch zuweisen an"
                @input="onAutoAssignRuleInput"
              >
                <template #options>
                  <optgroup
                    v-for="(rules, ruleGroup) in groupedAutoAssignRules"
                    :key="ruleGroup"
                    :label="ruleGroup"
                  >
                    <option
                      v-for="rule in rules"
                      :key="rule.id"
                      :value="rule.item_rule"
                    >
                      {{ rule.item_title }}
                    </option>
                  </optgroup>
                </template>
              </mia-feature-select>
            </div>

            <div class="flex items-center ml-6">
              <label class="flex flex-col">
                <span
                  v-tooltip="'Die Aufgabe wird im Kontext nur ein mal angelegt, z.B. pro Fahrzeug für das eine Aufgabe automatisch erstellt wird.'"
                  class="mb-1 tracking-wider text-gray-800"
                >Einzigartig?</span>

                <div>
                  <mia-toggle
                    :value="task.item_unique === 'Y'"
                    :labels="{
                      checked: 'Ja',
                      unchecked: 'Nein'
                    }"
                    :width="80"
                    :font-size="13"
                    @input="onUniqueInput"
                  />
                </div>
              </label>
            </div>
          </div>

          <div class="flex">
            <div class="flex items-center">
              <label class="flex flex-col">
                <span
                  v-tooltip="'Die Aufgabe erhält nach der Erstellung eine Deadline'"
                  class="mb-1 tracking-wider text-gray-800 inline-flex cursor-help"
                >
                  Deadline
                  <span class="font-normal text-gray-800 text-xs relative -mt-1">?</span>
                </span>

                <div>
                  <mia-toggle
                    :value="task.item_deadline === 'Y'"
                    :labels="{
                      checked: 'Ja',
                      unchecked: 'Nein'
                    }"
                    :width="80"
                    :font-size="13"
                    @input="onDeadlineInput"
                  />
                </div>
              </label>
            </div>

            <div
              v-if="task.item_deadline === 'Y'"
              class="ml-6 flex"
            >
              <div>
                <label
                  :for="`${task.id}_deadline_value_input`"
                  class="mb-1 mr-2 tracking-wider text-gray-800 block"
                >
                  Deadline ab Aufgabenstellung
                </label>

                <div class="flex">
                  <mia-feature-input
                    :id="`${task.id}_deadline_value_input`"
                    ref="deadlineValueInputRef"
                    :value="task.item_deadline_value"
                    min="0"
                    class="w-20"
                    type="number"
                    @input="onDeadlineValueInput"
                  />

                  <mia-feature-select
                    :value="task.item_deadline_unit"
                    class="ml-3"
                    @input="onDeadlineUnitInput"
                  >
                    <template #options>
                      <option
                        v-for="unit in deadlineUnits"
                        :key="unit.id"
                        :value="unit.item_unit"
                      >
                        {{ unit.item_title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
              </div>

              <div class="flex items-center">
                <label class="flex flex-col">
                  <span class="mb-1 tracking-wider text-gray-800">Abteilungsleiter informieren</span>
                  <div>
                    <mia-toggle
                      :value="task.item_inform_department_head === '1'"
                      :labels="{
                        checked: 'Ja',
                        unchecked: 'Nein'
                      }"
                      :width="80"
                      :font-size="13"
                      @input="onInformDepartmentHeadInput"
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div />

          <div class="flex">
            <div class="flex items-center">
              <label class="flex flex-col">
                <span
                  v-tooltip="`Die Aufgabe wird nach Erstellung erst nach einer bestimmten Wartezeit sichtbar${task.item_deadline === 'Y' ? '. Die Deadline gilt ab der eingestellten Wartezeit.' : ''}`"
                  class="mb-1 tracking-wider text-gray-800 inline-flex cursor-help"
                >
                  Wartezeit
                  <span class="font-normal text-gray-800 text-xs relative -mt-1">?</span>
                </span>

                <div>
                  <mia-toggle
                    :value="task.item_idle === 'Y'"
                    :labels="{
                      checked: 'Ja',
                      unchecked: 'Nein'
                    }"
                    :width="80"
                    :font-size="13"
                    @input="onIdleInput"
                  />
                </div>
              </label>
            </div>

            <div
              v-if="task.item_idle === 'Y'"
              class="ml-6 flex"
            >
              <div>
                <label
                  :for="`${task.id}_idle_value_input`"
                  class="mb-1 mr-2 tracking-wider text-gray-800 block"
                >
                  Wartezeit ab Aufgabenstellung
                </label>

                <div class="flex">
                  <mia-feature-input
                    :id="`${task.id}_idle_value_input`"
                    ref="idleValueInputRef"
                    :value="task.item_idle_value"
                    min="0"
                    class="w-20"
                    type="number"
                    @input="onIdleValueInput"
                  />

                  <mia-feature-select
                    :value="task.item_idle_unit"
                    class="ml-3"
                    @input="onIdleUnitInput"
                  >
                    <template #options>
                      <option
                        v-for="unit in idleUnits"
                        :key="unit.id"
                        :value="unit.item_unit"
                      >
                        {{ unit.item_title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
              </div>
            </div>
          </div>

          <div>
            <mia-feature-input
              type="number"
              :value="task.item_time_value"
              label="Zeit-Wert (AWs)"
              @input="onTimeValueInput"
            />
          </div>

          <div>
            <mia-feature-input
              type="number"
              :value="task.item_priority"
              label="Priorität"
              @input="onPriorityInput"
            />
          </div>

          <div>
            <label class="flex flex-col">
              <span class="mb-1 tracking-wider text-gray-800">Übergeordnete Aufgaben</span>
              <mia-multi-select
                :value="task.item_tasks_required"
                placeholder="Übergeordnete Aufgaben"
                :options="selectableTasks"
                track-by="item_title"
                label="item_title"
                flatten
                multiple
                @input="onRequiredTasksInput"
              />
            </label>
          </div>
        </div>

        <div class="mt-6">
          <div class="flex items-center justify-between">
            <h5 class="my-0 py-0 text-lg tracking-wide">
              Eingabefelder
            </h5>

            <mia-action-button
              ref="inputFieldActionButtonRef"
              label="Eingabefeld hinzufügen"
              :disabled="!hasAvailableInputFields"
            >
              <mia-action-button-divider
                v-for="(fields, inputFieldGroup) in groupedInputFields"
                :key="inputFieldGroup"
                :label="inputFieldGroup"
              >
                <mia-action-button-item
                  v-for="field in fields"
                  :key="field.id"
                  @click="addInputField(field)"
                >
                  {{ field.item_title }}
                </mia-action-button-item>
              </mia-action-button-divider>
            </mia-action-button>
          </div>

          <div
            v-if="inputFields.length > 0"
            class="mt-6 grid gap-3 rounded-sm border-2 border-dashed border-gray-400 p-2"
            :class="{
              'grid-cols-2': task.item_input_fields.length > 0,
              'grid-cols-1': task.item_input_fields.length === 0,
            }"
          >
            <div
              v-if="task.item_input_fields.length === 0"
              class="py-2 px-4 text-center text-sm"
            >
              Keine Eingabefelder hinzugefügt.
            </div>

            <div
              v-for="inputField in task.item_input_fields"
              :key="inputField.id"
              class="bg-blue-100 flex items-center py-2 px-4 border border-blue-200 rounded-sm"
            >
              <div class="text-lg tracking-wide flex-1">
                {{ getInputFieldLabel(inputField.id) }}
              </div>
              <label class="flex items-center font-normal cursor-pointer pt-1">
                <span class="mr-2">Muss ausgefüllt sein</span>
                <mia-toggle
                  :value="inputField.item_is_required === 'Y'"
                  :labels="{
                    checked: 'Ja',
                    unchecked: 'Nein'
                  }"
                  :width="80"
                  :font-size="13"
                  @input="onInputFieldIsRequiredInput(inputField, $event)"
                />
              </label>
              <div class="ml-4">
                <button
                  class="w-6 h-6 text-gray-700 hover:text-red-500 flex items-center justify-center focus:outline-none"
                  type="button"
                  @click="deleteInputField(inputField)"
                >
                  <mia-streamline-icon
                    class="w-4 h-4 fill-current"
                    src="close"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end">
          <button
            type="button"
            class="bg-red-100 text-red-500 border border-red-200 rounded-sm px-3 py-1 text-sm hover:border-red-600 hover:bg-red-500 hover:text-white focus:outline-none"
            @click="deleteTask"
          >
            Aufgabe löschen
          </button>
        </div>
      </div>
    </div>
    <div class="flex items-start">
      <button
        class="text-gray-700 w-10 h-10 flex items-center justify-center focus:outline-none hover:text-gray-900"
        type="button"
        @click="isCollapsed = !isCollapsed"
      >
        <mia-streamline-icon
          class="w-6 h-6 transform fill-current"
          :class="{
            'rotate-90': isCollapsed,
            'rotate-180': !isCollapsed
          }"
          src="arrow-up-1"
        />
      </button>
    </div>
  </div>
</template>

<style lang="scss">
.tooltip {
  .tooltip-inner {
    @apply bg-gray-800;
    @apply text-sm;
    @apply px-2;
    @apply py-1;
    @apply rounded;
    @apply shadow-lg;
  }

  .tooltip-arrow {
    border-top-color: theme('borderColor.gray.800');
  }

  &[x-placement^="bottom"] .tooltip-arrow {
    border-bottom-color: theme('borderColor.gray.800');
  }
}
</style>
